// pages/SAPSlocDetail.jsx — storage locations of one plant, with usage status.
// Source: procmap_org snapshot (plants[].slocs, monthly) or live check
// GET /api/sap-procmap/sloc?land=&werks= (T001L + MSEG last 3 months).
// Answers "which SLOC is free / used for what" without SE16 or e-mailing the core team.
import { useEffect, useMemo, useState } from 'react';
import { api } from '../context/AuthContext';

const T = {
  en: {
    title: 'Storage locations per plant', plant: 'Plant', search: 'Search code or name…', live: 'Check live', loading: 'Reading SAP…',
    src: { snap: 'Monthly snapshot', live: 'Live from SAP' }, period: 'Movements', code: 'SLoc', name: 'Description', items: 'Items', last: 'Last movement', top: 'Main movement types',
    st: { all: 'All', active: 'Active', idle: 'Idle', never: 'No movement' }, none: 'No storage location matches.', noData: 'No storage-location list in the snapshot yet. Press "Check live".',
    hint: 'Active = moved in the last 30 days · Idle = moved earlier in the period · No movement = defined in T001L but not used in the period (candidate to reuse, confirm with the business first).',
  },
  id: {
    title: 'Storage location per plant', plant: 'Plant', search: 'Cari kode atau nama…', live: 'Cek live', loading: 'Membaca SAP…',
    src: { snap: 'Snapshot bulanan', live: 'Live dari SAP' }, period: 'Pergerakan', code: 'SLoc', name: 'Deskripsi', items: 'Item', last: 'Pergerakan terakhir', top: 'Jenis mvt utama',
    st: { all: 'Semua', active: 'Aktif', idle: 'Diam', never: 'Tanpa pergerakan' }, none: 'Tidak ada storage location yang cocok.', noData: 'Snapshot belum berisi daftar storage location. Tekan "Cek live".',
    hint: 'Aktif = bergerak 30 hari terakhir · Diam = bergerak lebih awal dalam periode · Tanpa pergerakan = ada di T001L tapi tidak dipakai dalam periode (kandidat dipakai ulang, konfirmasi dulu ke user).',
  },
};

const ymd = s => (s && s.length === 8 ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6)}` : '');
function status(s, refDate) {
  if (!s.mv) return 'never';
  const ref = refDate ? new Date(ymd(refDate)) : new Date();
  const days = (ref - new Date(ymd(s.last))) / 86400000;
  return days <= 30 ? 'active' : 'idle';
}
const BADGE = { active: 'bg-emerald-50 text-emerald-700', idle: 'bg-amber-50 text-amber-700', never: 'bg-slate-100 text-slate-500' };

export default function SAPSlocDetail({ landKey, plants, lang, period }) {
  const L = lang === 'id' ? 'id' : 'en';
  const t = T[L];
  const [werks, setWerks] = useState(plants[0] ? plants[0].k : '');
  const [q, setQ] = useState('');
  const [flt, setFlt] = useState('all');
  const [live, setLive] = useState({});           // werks -> { slocs, from, to, at }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { if (!plants.find(p => p.k === werks) && plants[0]) setWerks(plants[0].k); }, [plants, werks]);

  const plant = plants.find(p => p.k === werks);
  const L0 = live[landKey + werks];
  const slocs = L0 ? L0.slocs : (plant && plant.slocs) || [];
  const refDate = L0 ? L0.to : (slocs.reduce((m, s) => (s.last > m ? s.last : m), ''));

  const rows = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return slocs.map(s => ({ ...s, st: status(s, refDate) }))
      .filter(s => flt === 'all' || s.st === flt)
      .filter(s => !ql || s.k.toLowerCase().includes(ql) || (s.n || '').toLowerCase().includes(ql));
  }, [slocs, q, flt, refDate]);
  const counts = useMemo(() => {
    const c = { all: slocs.length, active: 0, idle: 0, never: 0 };
    slocs.forEach(s => { c[status(s, refDate)]++; }); return c;
  }, [slocs, refDate]);

  async function checkLive() {
    setBusy(true); setErr('');
    try {
      const { data } = await api.get('/api/sap-procmap/sloc', { params: { land: landKey, werks }, timeout: 180000 });
      setLive(v => ({ ...v, [landKey + werks]: data }));
    } catch (e) { setErr((e.response && e.response.data && e.response.data.error) || e.message); }
    finally { setBusy(false); }
  }

  if (!plants.length) return null;
  const fmt = n => n.toLocaleString(L === 'id' ? 'id-ID' : 'en-US');

  return (
    <div className="flex flex-col gap-2 border border-slate-200 rounded-lg p-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mr-auto">{t.title}</div>
        <select value={werks} onChange={e => setWerks(e.target.value)} className="border border-slate-200 rounded-md px-2 py-1 text-[12.5px] bg-white">
          {plants.map(p => <option key={p.k} value={p.k}>{p.k} — {p.n}</option>)}
        </select>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t.search} className="border border-slate-200 rounded-md px-2 py-1 text-[12.5px] w-[180px]" />
        <button type="button" onClick={checkLive} disabled={busy || !werks}
          className="px-2.5 py-1 rounded-md text-[12px] font-semibold border bg-brand-600 text-white border-brand-600 disabled:opacity-60">
          {busy ? t.loading : `↻ ${t.live}`}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 text-[11.5px]">
        {['all', 'active', 'idle', 'never'].map(k => (
          <button key={k} type="button" onClick={() => setFlt(k)}
            className={`px-2 py-0.5 rounded-full border ${flt === k ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200'}`}>
            {t.st[k]} <span className="font-mono opacity-70">{counts[k]}</span>
          </button>
        ))}
        <span className="ml-auto text-slate-400">
          {L0 ? `${t.src.live} · ${t.period} ${ymd(L0.from)} – ${ymd(L0.to)}` : `${t.src.snap}${period ? ` · ${t.period} ${period}` : ''}`}
        </span>
      </div>

      {err && <div className="text-[12px] text-rose-600">{err}</div>}

      {!slocs.length ? (
        <div className="text-[12.5px] text-slate-500 py-2">{t.noData}</div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg max-h-[420px] overflow-y-auto">
          <table className="w-full text-[12.5px] border-collapse">
            <thead className="sticky top-0">
              <tr className="bg-slate-50 text-slate-500 text-[11px]">
                <th className="px-2.5 py-1.5 text-left font-semibold">{t.code}</th>
                <th className="px-2.5 py-1.5 text-left font-semibold">{t.name}</th>
                <th className="px-2.5 py-1.5 text-left font-semibold">Status</th>
                <th className="px-2.5 py-1.5 text-right font-semibold">{t.items}</th>
                <th className="px-2.5 py-1.5 text-left font-semibold whitespace-nowrap">{t.last}</th>
                <th className="px-2.5 py-1.5 text-left font-semibold">{t.top}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(s => (
                <tr key={s.k} className="border-t border-slate-100">
                  <td className="px-2.5 py-1.5 font-mono font-bold text-slate-800">{s.k}</td>
                  <td className="px-2.5 py-1.5 text-slate-700">{s.n || '—'}</td>
                  <td className="px-2.5 py-1.5"><span className={`px-1.5 py-[1px] rounded text-[11px] ${BADGE[s.st]}`}>{t.st[s.st]}</span></td>
                  <td className="px-2.5 py-1.5 text-right font-mono tabular-nums">{s.mv ? fmt(s.mv) : '—'}</td>
                  <td className="px-2.5 py-1.5 font-mono whitespace-nowrap">{ymd(s.last) || '—'}</td>
                  <td className="px-2.5 py-1.5">
                    {s.top && s.top.length ? (
                      <span className="flex flex-wrap gap-1">{s.top.map(([b, tx, n]) => (
                        <span key={b} title={tx} className="rounded border border-slate-200 bg-slate-50 px-1.5 text-[11px]">
                          <b className="font-mono">{b}</b> <span className="font-mono text-slate-400">{fmt(n)}</span>
                        </span>))}</span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={6} className="px-2.5 py-3 text-center text-slate-400">{t.none}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11.5px] text-slate-400 m-0">{t.hint}</p>
    </div>
  );
}
